import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withInputAPI } from '@glu/validation-react';
import { createUseStyles, withTheme, useDarkMode } from '@glu/theming';
import Dropdown from './Dropdown';
import TextInput from './TextInput/TextInput';
import { datesToString, stringToDates } from './utils/formatString.js';
import styles from './DatePicker.styles';

const useStyles = createUseStyles(styles);

const getInitialDateValue = (date) =>
  date ? (typeof date === 'string' ? moment(date) : date) : undefined;

const DatePicker = ({
  RangeSelectProps,
  className,
  dark: darkProp,
  dataQa,
  htmlId,
  name,
  onApply,
  onBlur,
  onChange,
  onFocus,
  onKeyDown,
  open: passedOpen,
  screenReaderLabel,
  setOpen: passedSetOpen,
  usePortal,
  value,
  wrapperClassName,
  ...rest
}) => {
  const [dateStart, dateEnd] = value;
  const classes = useStyles();
  const isDarkMode = useDarkMode();
  const applyDarkMode = darkProp || isDarkMode;

  const [open, setOpen] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(getInitialDateValue(dateStart));
  const [selectedEndDate, setSelectedEndDate] = useState(getInitialDateValue(dateEnd));
  const [selectedRangeName, setSelectedRangeName] = useState();
  const [confirmedStartDate, setConfirmedStartDate] = useState(getInitialDateValue(dateStart));
  const [confirmedEndDate, setConfirmedEndDate] = useState(getInitialDateValue(dateEnd));
  const [head, setHead] = useState(getInitialDateValue(dateStart) || moment());
  const [inputValue, setInputValue] = useState(
    datesToString(getInitialDateValue(dateStart), getInitialDateValue(dateEnd), rest)
  );

  const {
    allowedFormats,
    allowedRangeDelimiters,
    format,
    rangeDelimiter,
    singleDatePicker,
    timePicker,
    timePicker24Hour,
    timePickerRange,
    timePickerSeconds,
  } = rest;

  const formats = [format, ...allowedFormats];
  const rangeDelimiters = [rangeDelimiter, ...allowedRangeDelimiters];
  const [firstFocusedElement, setFirstFocusedElement] = useState(false);
  const fieldRef = useRef();
  const inputRef = useRef();

  useEffect(() => {
    const startDate = getInitialDateValue(dateStart);
    const endDate = getInitialDateValue(dateEnd);

    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);
    setConfirmedStartDate(startDate);
    setConfirmedEndDate(endDate);

    // head should be valid date to avoid exceptions inside child components
    setHead(startDate?.isValid() ? startDate : moment());

    setInputValue(
      datesToString(startDate, endDate, {
        format,
        rangeDelimiter,
        singleDatePicker,
        timePicker,
        timePicker24Hour,
        timePickerRange,
        timePickerSeconds,
      })
    );
  }, [
    dateEnd,
    dateStart,
    singleDatePicker,
    format,
    timePicker,
    timePickerRange,
    timePickerSeconds,
    timePicker24Hour,
    rangeDelimiter,
  ]);

  const handleApply = useCallback(
    (start, end) => {
      if (start?.isValid() && end?.isValid()) {
        if (open) {
          setConfirmedStartDate(start);
          setConfirmedEndDate(end);
          onChange(name, [start && moment(start), end && moment(end)], true);
          onApply(name, [start && moment(start), end && moment(end)]);
          setInputValue(datesToString(start, end, rest));
          (passedSetOpen ?? setOpen)(false);
        } else {
          (passedSetOpen ?? setOpen)(true);
        }
      } else {
        onChange(name, [], true);
        onApply(name, []);
        (passedSetOpen ?? setOpen)(false);
      }
      setFirstFocusedElement(false);
    },
    [open, onChange, name, onApply, rest, passedSetOpen]
  );

  const handleClose = useCallback(() => {
    /* istanbul ignore next */
    if (!(passedOpen ?? open)) return;
    (passedSetOpen ?? setOpen)(false);
    setSelectedStartDate(confirmedStartDate);
    setSelectedEndDate(confirmedEndDate);
    setFirstFocusedElement(false);
  }, [passedOpen, open, passedSetOpen, confirmedStartDate, confirmedEndDate]);

  const uniqueId = useMemo(
    () => htmlId || `datepicker-${(Math.random() * 100000).toFixed()}`,
    [htmlId]
  );

  return (
    <div
      ref={fieldRef}
      className={`${classes.datepicker} ${className}`}
      data-qa={dataQa ? `${dataQa}` : `datepicker-${htmlId}`}
    >
      <TextInput
        {...{
          dark: applyDarkMode,
          format,
          formats,
          handleApply,
          handleClose,
          htmlId: uniqueId,
          inputValue,
          name,
          onChange,
          onFocus,
          inputRef,
          onKeyDown,
          open: passedOpen ?? open,
          onBlur,
          rangeDelimiters,
          screenReaderLabel,
          selectedEndDate,
          selectedStartDate,
          setConfirmedEndDate,
          setConfirmedStartDate,
          setFirstFocusedElement,
          setHead,
          setInputValue,
          setOpen: passedSetOpen ?? setOpen,
          setSelectedEndDate,
          setSelectedStartDate,
          ...rest,
        }}
      />
      {(passedOpen ?? open) && (
        <Dropdown
          fieldRef={fieldRef}
          firstFocusedElement={firstFocusedElement}
          inputRef={inputRef}
          onClose={handleClose}
          setFirstFocusedElement={setFirstFocusedElement}
          {...{
            RangeSelectProps,
            dark: applyDarkMode,
            head,
            htmlId: uniqueId,
            onApply: handleApply,
            onBlur,
            screenReaderLabel,
            selectedEndDate,
            selectedRangeName,
            selectedStartDate,
            setHead,
            setSelectedEndDate,
            setSelectedRangeName,
            setSelectedStartDate,
            usePortal,
            ...rest,
          }}
        />
      )}
    </div>
  );
};

DatePicker.propTypes = {
  /** Only used in the RangeLayout styles */
  RangeSelectProps: PropTypes.shape({ width: PropTypes.number }),

  /**
   * Date format strings used to parse the dates typed into the text input. If this is passed all
   * strings typed into the date picker will be parsed by moment using strictMode against these
   * formats only. If the string does not parse to a valid moment the moment will be set invalid.
   * const dateFormats = ['MM/DD/YYYY', 'MM/DD/YY', 'MM'];
   * @example  moment(date, dateFormats, true);
   */
  allowedFormats: PropTypes.arrayOf(PropTypes.string),

  /** Support a list of delimiters used to separate date string in text input */
  allowedRangeDelimiters: PropTypes.arrayOf(PropTypes.string),

  /** Force the position of the dropdown to align to the right side of the input */
  alwaysAlignRight: PropTypes.bool,

  /** Force the position of the dropdown to be above the input */
  alwaysOpenUp: PropTypes.bool,

  /** Toggle whether to always show calendar when a range is selected. */
  alwaysShowCalendars: PropTypes.bool,

  /** Toggle whether to calendars always display as the mobile layout (stacked). */
  alwaysStackCalendars: PropTypes.bool,

  /** Custom classes for the apply button */
  applyButtonClasses: PropTypes.string,

  /** Hide the apply and cancel buttons, and automatically apply a new selection */
  autoApply: PropTypes.bool,

  /** Custom classes for container that wraps action buttons */
  buttonClasses: PropTypes.string,

  /** Custom classes for the cancel button */
  cancelButtonClasses: PropTypes.string,

  /** Class passed to datepicker div element for extra decoration. */
  className: PropTypes.string,

  /** Option to disable user's ability to close the datepicker by clicking away from it.
   * useful when the datepicker overflows a scrolling container
   */
  clickOutsideToClose: PropTypes.bool,

  /** Render in dark mode */
  dark: PropTypes.bool,

  /** String given to parent elements data-qa html attribute for easy targeting */
  dataQa: PropTypes.string,

  /** The latest date a user may select. Must be ISO format 'YYYY-MM-DD' */
  dateMax: PropTypes.string,

  /** The earliest date a user may select. Must be ISO format 'YYYY-MM-DD' */
  dateMin: PropTypes.string,

  /**
   * An optional class that wraps the dropdown which is especially useful
   * when used in combination with usePortal: true
   */
  dropdownClassName: PropTypes.string,

  /** The number of days ahead of the selected start date
   * to set dateMax to after making initial selection */
  dynamicMaxRange: PropTypes.number,

  /** Error message. will add styles to input if present. */
  errors: PropTypes.arrayOf(PropTypes.string),

  /** Do not reposition dropdown to the right of the input */
  forcePositionX: PropTypes.bool,

  /** Do not reposition dropdown above the input */
  forcePositionY: PropTypes.bool,

  /** Format to display date string in input
   * a function should return a string and accept moment dates as parameters
   * @example const format = (date) => date.format('MM/DD/YYYY');
   */
  format: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /** Function which is passed each visible day and returns string of classNames. */
  getClassNamesForDate: PropTypes.func,

  /** string assigned to HTML id attribute of component */
  htmlId: PropTypes.string,

  /* Function which is passed each visible date and returns boolean for if it is selectable. */
  isSelectableDate: PropTypes.func,

  /** Display weeks as ISO week numbers; weekNumbers must also be true. */
  isoWeekNumbers: PropTypes.bool,

  /** String used for label value */
  labelText: PropTypes.node,

  /** Toggle to control calendar navigation. Defaults to true. Can be set to
   * false to navigate calendars independently. */
  linkedCalendars: PropTypes.bool,

  /** Show select dropdown for the Month in the calendar header */
  monthDropdown: PropTypes.bool,

  /** string passed to html name attribute of text input */
  name: PropTypes.string,

  /** The number of calendars to display side by side. */
  numberOfMonths: PropTypes.number,

  /** Function that triggers once a date is selected and Apply or Enter button is clicked */
  onApply: PropTypes.func,

  /** Function that triggers once there was a click outside of DatePicker or close button was
   * clicked on Dropdown */
  onBlur: PropTypes.func,

  /** Function that triggers once a date is changed in the input and Apply or
   * Enter button is clicked */
  onChange: PropTypes.func,

  /** Callback called when the datepicker's date entry text field receives focus */
  onFocus: PropTypes.func,

  /** Function which is passed to handle key down events on Text Input Field. */
  onKeyDown: PropTypes.func,

  /** triggers when preset range is selected. helpful for distinguishing date ranges that appear
   * the same. i.e. last month vs last quarter */
  onPresetSelection: PropTypes.func,

  /**
   * When passed, will not use internal state to control the opening and closing of the grid.
   */
  open: PropTypes.bool,

  /** String passed as html placeholder text for input. */
  placeholder: PropTypes.string,

  /** String of x position */
  positionX: PropTypes.string,

  /** String of y position */
  positionY: PropTypes.string,

  /** Delimiter used to separate date string in text input */
  rangeDelimiter: PropTypes.string,

  /** Object with list of predefined ranges */
  rangePresets: PropTypes.shape({}),

  /** Toggle range preset feature */
  ranges: PropTypes.bool,

  /** Toggle to hide the label for all but screenreaders */
  screenReaderLabel: PropTypes.bool,

  /**
   * When passed, this function will be called in place of the internal setOpen.
   * The prop should not be passed unless you pass open.
   */
  setOpen: PropTypes.func,

  /** Show "custom range" option to display calendar */
  showCustomRangeLabel: PropTypes.bool,

  /** Interacting with calendar sets a single date. */
  singleDatePicker: PropTypes.bool,

  /** Function to override the existing internal stringToDates function
   * @param {string} value - The string to be transformed
   * @param {string} rangeDelimiter - Indicates the separation between start and end dates
   * @param {string} format - The formatting pattern provided as a prop
   *
   * Expected to return array with start and end dates
   * [start, end]
   */
  stringToDates: PropTypes.func,

  /** Show timePicker */
  timePicker: PropTypes.bool,

  /** Display 24 hour military style time. Also hides am/pm selector. */
  timePicker24Hour: PropTypes.bool,

  /** Default endTime time to display in timePicker */
  timePickerDefaultEndTime: PropTypes.shape({
    hour: PropTypes.number,
    minute: PropTypes.number,
    second: PropTypes.number,
  }),

  /** Default startTime time to display in timePicker */
  timePickerDefaultStartTime: PropTypes.shape({
    hour: PropTypes.number,
    minute: PropTypes.number,
    second: PropTypes.number,
  }),

  /** Size of increments for minute selector. */
  timePickerIncrement: PropTypes.number,

  /** Show two timepickers to define a time range */
  timePickerRange: PropTypes.bool,

  /** Show seconds selector. timePicker or timePickerRange must be set */
  timePickerSeconds: PropTypes.bool,

  /** When enabled the dropdown will render in the root of document.body
   * Used for extreme edges cases when the datepicker is in a container with overflow hidden
   * Breaks keyboard A11y and has decreased performance */
  usePortal: PropTypes.bool,

  /** The start and end of initial selected date range. Must be ISO format 'YYYY-MM-DD' */
  value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),

  /** Display column left of calendar with week numbers */
  weekNumbers: PropTypes.bool,

  /** Class passed to parent wrapper ClickOutside element for extra decoration. */
  wrapperClassName: PropTypes.string,

  /** Show select dropdown for the Year in the calendar header */
  yearDropdown: PropTypes.bool,

  /** The maximum year in the select year dropdown */
  yearDropdownMaxYear: PropTypes.number,

  /** The minimum year in the select year dropdown */
  yearDropdownMinYear: PropTypes.number,
};

export const defaultProps = {
  RangeSelectProps: {},
  allowedFormats: [],
  allowedRangeDelimiters: [],
  alwaysAlignRight: false,
  alwaysOpenUp: false,
  alwaysShowCalendars: false,
  alwaysStackCalendars: false,
  applyButtonClasses: '',
  autoApply: false,
  buttonClasses: '',
  cancelButtonClasses: '',
  className: '',
  clickOutsideToClose: true,
  dark: false,
  dataQa: '',
  dateMax: moment().add(50, 'y').format('YYYY-MM-DD'),
  dateMin: moment().add(-50, 'y').format('YYYY-MM-DD'),
  dropdownClassName: undefined,
  dynamicMaxRange: undefined,
  errors: undefined,
  forcePositionX: false,
  forcePositionY: false,
  format: 'D MMM YYYY',
  getClassNamesForDate: () => '',
  htmlId: '',
  isSelectableDate: () => false,
  isoWeekNumbers: false,
  labelText: '',
  linkedCalendars: true,
  monthDropdown: false,
  name: '',
  numberOfMonths: 1,
  onApply: () => {},
  onBlur: () => {},
  onChange: () => {},
  onFocus: () => {},
  onKeyDown: () => '',
  onPresetSelection: () => {},
  open: undefined,
  placeholder: '',
  positionX: 'right',
  positionY: 'down',
  rangeDelimiter: ' - ',
  rangePresets: {
    'Last 30 Days': [
      moment().subtract(30, 'day').format('YYYY-MM-DD'),
      moment().format('YYYY-MM-DD'),
    ],
    'Last 7 Days': [
      moment().subtract(7, 'day').format('YYYY-MM-DD'),
      moment().format('YYYY-MM-DD'),
    ],
    'Last Month': [
      moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
      moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
    ],
    'This Month': [moment().startOf('month').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')],
    Today: [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')],
    Yesterday: [
      moment().subtract(1, 'day').format('YYYY-MM-DD'),
      moment().subtract(1, 'day').format('YYYY-MM-DD'),
    ],
  },
  ranges: false,
  screenReaderLabel: false,
  setOpen: undefined,
  showCustomRangeLabel: false,
  singleDatePicker: false,
  stringToDates,
  timePicker: false,
  timePicker24Hour: false,
  timePickerDefaultEndTime: {
    hour: 23,
    minute: 59,
    second: 59,
  },
  timePickerDefaultStartTime: {
    hour: 0,
    minute: 0,
    second: 0,
  },
  timePickerIncrement: 1,
  timePickerRange: false,
  timePickerSeconds: false,
  usePortal: false,
  value: [],
  weekNumbers: false,
  wrapperClassName: '',
  yearDropdown: false,
  yearDropdownMaxYear: parseInt(moment().add(50, 'y').format('YYYY'), 10),
  yearDropdownMinYear: parseInt(moment().add(-50, 'y').format('YYYY'), 10),
};
DatePicker.defaultProps = defaultProps;

const StyledDatePicker = withTheme(DatePicker);
const InputApiDatePicker = withInputAPI(StyledDatePicker);
export const BareDatePicker = DatePicker;
StyledDatePicker.displayName = 'DatePicker';
InputApiDatePicker.displayName = 'DatePicker';
export default InputApiDatePicker;
